/**
 * AdminABMHCOneComCMS — CMS for the ABMHC one.com public website.
 *
 * Full content control over the PHP-rendered abmhc.org website:
 * hero copy, statistics (progress %, beds, milestones-complete), per-page
 * copy blocks, milestones with progress %, gallery, plus the admin queues
 * for contact submissions and newsletter subscribers.
 *
 * Backend: /api/abmhc/onecom/admin/site/abmhc/*
 * Public read (consumed by the PHP site): /api/v1/sites/abmhc/public
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trash2, Plus, RotateCcw, Save, Globe, Users, Mail } from 'lucide-react';
import { getToken } from '@/lib/authToken';
import AbmhcEmbedCard from '@/components/abmhc/AbmhcEmbedCard';
import SiteConnectionCard from '@/components/SiteConnectionCard';
import { MediaUpload } from '@/components/MediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const SLUG = 'abmhc';
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const PAGES = ['about', 'progress', 'milestones', 'gallery', 'transparency', 'contact'];

export default function AdminABMHCOneComCMS() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [siteRes, submRes, subsRes] = await Promise.all([
        axios.get(`${API}/api/abmhc/onecom/admin/site/${SLUG}`, { headers: authH() }),
        axios.get(`${API}/api/abmhc/onecom/admin/site/${SLUG}/submissions`, { headers: authH() }),
        axios.get(`${API}/api/abmhc/onecom/admin/site/${SLUG}/subscribers`, { headers: authH() }),
      ]);
      setSite(siteRes.data);
      setSubmissions(submRes.data || []);
      setSubscribers(subsRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load site content');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!site) return;
    setBusy(true);
    try {
      const payload = {
        site: site.site,
        hero: site.hero,
        stats: site.stats,
        pages: site.pages,
        milestones: site.milestones,
        gallery: site.gallery,
      };
      const r = await axios.put(
        `${API}/api/abmhc/onecom/admin/site/${SLUG}`, payload,
        { headers: authH() });
      setSite(r.data);
      toast.success('Saved. The one.com website will pick up changes within 5 minutes.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const resetDefaults = async () => {
    if (!confirm('Reset all editable content to defaults? Contact submissions and subscribers are NOT affected.')) return;
    try {
      const r = await axios.post(
        `${API}/api/abmhc/onecom/admin/site/${SLUG}/reset`, {},
        { headers: authH() });
      setSite(r.data);
      toast.success('Restored to defaults');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
  };

  if (!site) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-300 p-6 flex items-center justify-center">
        {busy ? 'Loading…' : 'Content not available.'}
      </div>
    );
  }

  const setField = (path, value) => {
    setSite((prev) => {
      const next = structuredClone(prev);
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6" data-testid="admin-abmhc-onecom-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <Globe className="h-7 w-7 text-amber-300" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">ABMHC one.com Website CMS</h1>
            <p className="text-sm text-slate-400">
              Live content for the ABMHC public site (hero copy, statistics, milestones, per-page copy).
              Updates are cached for 5 min on one.com and then refresh automatically.
            </p>
          </div>
          <Button variant="outline" onClick={resetDefaults}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="abmhc-cms-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button onClick={save} disabled={busy} data-testid="abmhc-cms-save">
            <Save className="h-4 w-4 mr-1" /> {busy ? 'Saving…' : 'Save'}
          </Button>
        </header>

        <SiteConnectionCard slug="abmhc" brandColor="amber"
          publicUrl={site.site?.public_url || ''}
          onUrlChange={(u) => setField('site.public_url', u)} />

        <AbmhcEmbedCard />

        <Tabs defaultValue="hero">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="hero" data-testid="tab-hero">Hero &amp; Site</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">Statistics</TabsTrigger>
            <TabsTrigger value="milestones" data-testid="tab-milestones">Milestones</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">Page Copy</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-subm">Enquiries ({submissions.length})</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subs">Newsletter ({subscribers.length})</TabsTrigger>
          </TabsList>

          {/* HERO & SITE INFO */}
          <TabsContent value="hero" className="space-y-4">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm">Site info</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" value={site.site.name} onChange={(v) => setField('site.name', v)} testid="fld-site-name" />
                <Field label="Short name" value={site.site.short_name} onChange={(v) => setField('site.short_name', v)} testid="fld-site-short" />
                <Field label="Phone" value={site.site.phone} onChange={(v) => setField('site.phone', v)} testid="fld-site-phone" />
                <Field label="Location" value={site.site.location} onChange={(v) => setField('site.location', v)} testid="fld-site-loc" />
                <Field label="Public site URL (production one.com)" value={site.site.public_url} onChange={(v) => setField('site.public_url', v)} testid="fld-site-public" placeholder="https://…" wide />
                <Field label="Donation URL (external)" value={site.site.donation_url} onChange={(v) => setField('site.donation_url', v)} testid="fld-site-donation" placeholder="https://…" wide />
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm">Hero</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Field label="Eyebrow" value={site.hero.eyebrow} onChange={(v) => setField('hero.eyebrow', v)} testid="fld-hero-eyebrow" />
                <Field label="Headline" value={site.hero.title} onChange={(v) => setField('hero.title', v)} testid="fld-hero-title" />
                <FieldArea label="Copy" value={site.hero.copy} onChange={(v) => setField('hero.copy', v)} testid="fld-hero-copy" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Statistics (shown as 4 tiles on the home page)</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('stats', [...site.stats, { value: '', label: '' }])}
                        data-testid="stats-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {site.stats.map((s, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-end"
                       data-testid={`stat-row-${i}`}>
                    <Field label="Value" value={s.value}
                           onChange={(v) => setField(`stats.${i}.value`, v)}
                           testid={`stat-${i}-value`} placeholder="42%" />
                    <Field label="Label" value={s.label}
                           onChange={(v) => setField(`stats.${i}.label`, v)}
                           testid={`stat-${i}-label`} placeholder="Overall progress" />
                    <Button variant="outline" size="sm"
                            className="border-rose-800 text-rose-300 hover:bg-rose-500/10 mb-0.5"
                            onClick={() => setField('stats', site.stats.filter((_, x) => x !== i))}
                            data-testid={`stat-del-${i}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MILESTONES */}
          <TabsContent value="milestones">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Milestones (progress % drives the bars on the site)</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('milestones', [...site.milestones,
                          { phase: '', title: '', status: 'Planned', progress: 0 }])}
                        data-testid="mst-add">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {site.milestones.map((m, i) => (
                  <div key={i} className="grid grid-cols-[80px_2fr_140px_120px_auto] gap-3 items-end"
                       data-testid={`mst-row-${i}`}>
                    <Field label="Phase" value={m.phase}
                           onChange={(v) => setField(`milestones.${i}.phase`, v)}
                           testid={`mst-${i}-phase`} placeholder="01" />
                    <Field label="Title" value={m.title}
                           onChange={(v) => setField(`milestones.${i}.title`, v)}
                           testid={`mst-${i}-title`} />
                    <Field label="Status" value={m.status}
                           onChange={(v) => setField(`milestones.${i}.status`, v)}
                           testid={`mst-${i}-status`} placeholder="Completed" />
                    <div>
                      <Label className="text-xs text-slate-400">Progress %</Label>
                      <Input type="number" min={0} max={100} value={m.progress ?? 0}
                             onChange={(e) => setField(`milestones.${i}.progress`,
                               Math.max(0, Math.min(100, parseInt(e.target.value || '0', 10))))}
                             className="bg-slate-900 border-slate-800 mt-1"
                             data-testid={`mst-${i}-progress`} />
                    </div>
                    <Button variant="outline" size="sm"
                            className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                            onClick={() => setField('milestones', site.milestones.filter((_, x) => x !== i))}
                            data-testid={`mst-del-${i}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAGE COPY */}
          <TabsContent value="pages" className="space-y-4">
            {PAGES.map((slug) => {
              const p = site.pages?.[slug] || { eyebrow: '', title: '', copy: '' };
              return (
                <Card key={slug} className="bg-slate-900/60 border-slate-800">
                  <CardHeader><CardTitle className="text-sm capitalize">/{slug}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <Field label="Eyebrow" value={p.eyebrow}
                           onChange={(v) => setField(`pages.${slug}.eyebrow`, v)}
                           testid={`page-${slug}-eyebrow`} />
                    <Field label="Title" value={p.title}
                           onChange={(v) => setField(`pages.${slug}.title`, v)}
                           testid={`page-${slug}-title`} />
                    <FieldArea label="Copy" value={p.copy}
                               onChange={(v) => setField(`pages.${slug}.copy`, v)}
                               testid={`page-${slug}-copy`} />
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Gallery images (public URLs)</CardTitle>
                <Button size="sm" variant="outline" className="border-slate-700"
                        onClick={() => setField('gallery', [...(site.gallery || []),
                          { url: '', alt: '', caption: '' }])}
                        data-testid="gal-add">
                  <Plus className="h-3 w-3 mr-1" /> Add image
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(site.gallery || []).length === 0 && (
                  <p className="text-xs text-slate-500">No gallery items yet — click Add image.</p>
                )}
                {(site.gallery || []).map((g, i) => (
                  <div key={i} className="grid grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end"
                       data-testid={`gal-row-${i}`}>
                    <MediaUpload value={g.url} onChange={(url) => setField(`gallery.${i}.url`, url)}
                           purpose="gallery" label="Image" testId={`gal-${i}-url`} previewClassName="h-16" />
                    <Field label="Alt" value={g.alt}
                           onChange={(v) => setField(`gallery.${i}.alt`, v)}
                           testid={`gal-${i}-alt`} />
                    <Field label="Caption" value={g.caption}
                           onChange={(v) => setField(`gallery.${i}.caption`, v)}
                           testid={`gal-${i}-caption`} />
                    <Button variant="outline" size="sm"
                            className="border-rose-800 text-rose-300 hover:bg-rose-500/10"
                            onClick={() => setField('gallery', site.gallery.filter((_, x) => x !== i))}
                            data-testid={`gal-del-${i}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUBMISSIONS */}
          <TabsContent value="submissions">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" /> Contact enquiries
              </CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {submissions.length === 0 && <p className="text-xs text-slate-500">No enquiries yet.</p>}
                {submissions.map((s) => (
                  <div key={s.id} className="border border-slate-800 rounded-md p-3 bg-slate-800/30"
                       data-testid={`subm-${s.id}`}>
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>{s.name} · {s.email} · <Badge variant="outline" className="ml-1 text-[10px]">{s.topic}</Badge></span>
                      <span>{s.submitted_at?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                    <p className="text-sm whitespace-pre-line">{s.message}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUBSCRIBERS */}
          <TabsContent value="subscribers">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" /> Newsletter subscribers
              </CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {subscribers.length === 0 && <p className="text-xs text-slate-500">No subscribers yet.</p>}
                {subscribers.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b border-slate-800 py-1.5 text-sm"
                       data-testid={`sub-${s.id}`}>
                    <span>{s.email}</span>
                    <span className="text-xs text-slate-500">
                      {s.source} · {s.subscribed_at?.slice(0, 10)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, placeholder = '', wide = false }) => (
  <div className={wide ? 'md:col-span-2' : ''}>
    <Label className="text-xs text-slate-400">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
           className="bg-slate-900 border-slate-800 mt-1"
           placeholder={placeholder} data-testid={testid} />
  </div>
);

const FieldArea = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs text-slate-400">{label}</Label>
    <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)}
              className="bg-slate-900 border-slate-800 mt-1" data-testid={testid} />
  </div>
);
